import { useEffect } from "react";
import { CalendarClock, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getActiveCampaigns, CAMPAIGN_TERMS, CAMPAIGN_TERMS_TITLE } from "@/data/campaigns";
import { buildWhatsAppUrl } from "@/lib/site";
import { useSettings } from "@/context/SettingsContext";

function formatDateTR(iso) {
  return new Date(iso + "T12:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487 2.981 1.287 2.981.858 3.518.804.537-.054 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /></svg>
);

export default function Campaigns() {
  const { settings } = useSettings();
  const campaigns = getActiveCampaigns();

  useEffect(() => { document.title = "Kampanyalar | VoyaCar Rental"; }, []);

  return (
    <div>
      <div className="bg-[#171717] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-bold">Kampanyalar</h1>
          <p className="mt-2 text-neutral-300">VoyaCar Rental'ın güncel avantaj ve fırsatları.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {campaigns.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-neutral-200" data-testid="campaigns-empty">
            <Tag className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500">Şu anda aktif kampanya bulunmuyor. Yeni fırsatlar için bizi takip edin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-neutral-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-testid={`campaign-card-${c.id}`}>
                <div className="aspect-[16/9] overflow-hidden bg-neutral-100">
                  <picture>
                    <source media="(max-width: 767px)" srcSet={c.gorselMobil || c.gorselMasaustu} />
                    <img src={c.gorselMasaustu} alt={c.baslik} loading="lazy" className="w-full h-full object-cover" />
                  </picture>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h2 className="text-lg font-bold text-[#171717]">{c.baslik}</h2>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">{c.aciklama}</p>
                  {c.gecerlilik && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500">
                      <CalendarClock className="h-4 w-4 text-[#A11016]" />
                      <span>{formatDateTR(c.gecerlilik)} tarihine kadar geçerli</span>
                    </div>
                  )}
                  <a
                    href={buildWhatsAppUrl(settings.whatsapp, `Merhaba VoyaCar Rental, "${c.baslik}" kampanyası hakkında bilgi almak istiyorum.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4"
                    data-testid={`campaign-whatsapp-${c.id}`}
                  >
                    <Button className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white gap-2">
                      <WhatsAppIcon className="h-4 w-4" /> WhatsApp'tan Sor
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8" data-testid="campaign-terms">
          <h2 className="text-xl font-bold text-[#171717] mb-4">{CAMPAIGN_TERMS_TITLE}</h2>
          <div className="space-y-3">
            {CAMPAIGN_TERMS.map((t, i) => (
              <p key={i} className="text-sm text-neutral-600 leading-relaxed">{t}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
